import React from "react";
import { FormControl, FormErrorMessage } from "@UI/Components/Form/Form";

import { Checkbox } from "@UI/Components/Checkbox/Checkbox";
import { Text } from "@UI/Components/Text/Text";
import { Flex } from "@UI/Components/Flex/Flex";
import { Tag } from "@UI/Components/Tag/Tag";
import { GenericInput } from "@modules/Shared/Components/Forms/Inputs/GenericInput";
import { Button } from "@UI/Components/Button/Button";
import { Icon } from "@UI/Components/Icon/Icon";
import { Trash } from "@UI/Components/Icons/Trash";
import { VStack } from "@UI/Components/Stack/Stack";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import messages from "@modules/ServiceProviders/Components/messages";
import i18n from "@I18n/services/i18n";
import { Card } from "@UI/Components/Card/Card";
import { ICompanyInfoForm } from "@modules/ServiceProviders/types";
import { Control, FieldErrors, UseFormRegister, Controller } from "react-hook-form";

interface ICompanyDetailsFormProps {
  register: UseFormRegister<ICompanyInfoForm>;
  control: Control<ICompanyInfoForm>;
  errors: FieldErrors<ICompanyInfoForm>;
  hasCompanyInfo: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  onOpenDeleteModal: () => void;
  onRegisterAsEnterpriseChange?: (checked: boolean, onChange: (value: boolean) => void) => void;
}

export const CompanyDetailsForm: React.FC<ICompanyDetailsFormProps> = ({
  register,
  control,
  errors,
  hasCompanyInfo,
  isSaving,
  isDeleting,
  onOpenDeleteModal,
  onRegisterAsEnterpriseChange,
}) => {
  return (
    <VStack spacing={4} alignItems="stretch">
      <Card>
        <VStack spacing={6} alignItems="stretch">
          <Controller
            name="i_register_as_enterprise"
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <FormControl isInvalid={!!errors.i_register_as_enterprise}>
                <Checkbox
                  isChecked={value}
                  onChange={(e) => {
                    if (onRegisterAsEnterpriseChange) {
                      onRegisterAsEnterpriseChange(e.target.checked, onChange);
                    } else {
                      onChange(e.target.checked);
                    }
                  }}
                  colorScheme="seaGreen"
                  isInvalid={!!errors.i_register_as_enterprise}
                  isDisabled={hasCompanyInfo}
                  _disabled={{
                    opacity: 0.8,
                    cursor: "not-allowed",
                  }}
                  sx={{
                    "& .chakra-checkbox__control[data-disabled][data-checked]": {
                      bg: "seaGreen.500",
                      borderColor: "seaGreen.500",
                      color: "white",
                    },
                  }}
                >
                  <Text as="span" fontSize="md" color={errors.i_register_as_enterprise ? "red.500" : "inherit"}>
                    <FormattedMessage {...messages.companyInfoSignUpAsCompany} />
                  </Text>
                </Checkbox>
                <FormErrorMessage>
                  <FormattedMessage {...messages.requiredField} />
                </FormErrorMessage>
              </FormControl>
            )}
          />

          <Flex
            bg="blue.50"
            p={4}
            borderRadius="md"
            alignItems="center"
          >
            <Tag bg="#0098FE" color="white" mr={3} fontWeight="bold" flexShrink={0}>
              PRO
            </Tag>
            <Text fontSize="sm" color="gray.600" mb={0}>
              <FormattedMessage {...messages.companyInfoProInvoiceInfo} />
            </Text>
          </Flex>

          <GenericInput
            id="vat_number"
            label={i18n.formatMessage(messages.vatNumberLabel)}
            registerObj={register("vat_number")}
            isError={!!errors.vat_number}
            errorMessage={errors.vat_number?.message}
          />

          <GenericInput
            id="company_name"
            label={i18n.formatMessage(messages.companyNameLabel)}
            registerObj={register("company_name")}
            isError={!!errors.company_name}
            errorMessage={errors.company_name?.message}
          />
          <GenericInput
            id="address"
            label={i18n.formatMessage(messages.addressLabel)}
            registerObj={register("address")}
            isError={!!errors.address}
            errorMessage={errors.address?.message}
          />
          <GenericInput
            id="postcode"
            label={i18n.formatMessage(messages.postalCodeLabel)}
            registerObj={register("postcode")}
            isError={!!errors.postcode}
            errorMessage={errors.postcode?.message}
            type="number"
            onKeyDown={(e) => {
              const allowedKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Enter"];
              if (
                !/[0-9]/.test(e.key) &&
                !allowedKeys.includes(e.key) &&
                !e.ctrlKey &&
                !e.metaKey
              ) {
                e.preventDefault();
              }
            }}
          />
          <GenericInput
            id="city"
            label={i18n.formatMessage(messages.cityLabel)}
            registerObj={register("city")}
            isError={!!errors.city}
            errorMessage={errors.city?.message}
          />

          <Button
            type="submit"
            colorScheme="seaGreen"
            size="lg"
            mt={4}
            isLoading={isSaving}
          >
            <FormattedMessage {...messages.saveButton} />
          </Button>
        </VStack>
      </Card>

      {hasCompanyInfo && (
        <Button
          variant="unstyled"
          color="red.400"
          display="flex"
          alignItems="center"
          justifyContent="center"
          mt={4}
          fontWeight="normal"
          onClick={onOpenDeleteModal}
          isLoading={isDeleting}
        >
          <Icon as={Trash} mr={2} />
          <FormattedMessage {...messages.deleteBusinessAccount} />
        </Button>
      )}
    </VStack>
  );
};
